import { forwardRef, useMemo, useRef, useState, type ReactNode, type Ref } from "react";
import { SimplePageLayout } from "../../components/simplePage/simplePageLayout.tsx";
import { DataGrid, type DataGridHandle } from "../../components/dataGrid/dataGrid.tsx";
import { GridEditModal } from "../../components/editSearch/gridEditModal.tsx";
import { useGridEditDialog } from "../../hooks/useGridEditDialog.ts";
import { useAuth } from "../../auth/authContext.tsx";
import { ModuleId } from "../../types/session.ts";
import styles from "../../styles/formPage.module.scss";
import { EditProductMovie } from "./editProductMovie.tsx";
import { EditProductAccess } from "./editProductAccess.tsx";
import { EditProductCS } from "./editProductCS.tsx";
import { EditProductNeoCaddie } from "./editProductNeoCaddie.tsx";
import { EditProductRobotics } from "./editProductRobotics.tsx";
import type { EditorState, ProductEditorHandle, ProductEditorPrefill } from "./editProductShared.ts";

function getModuleConfig(moduleId: number) {
  switch (moduleId) {
    case ModuleId.Rent:
    case ModuleId.Game:
    case ModuleId.GameRent:
      return { selectionColumn: "iProductID" };
    case ModuleId.CompuShop:
      return { selectionColumn: "ID" };
    case ModuleId.NeoCaddie:
      return { selectionColumn: "ID" };
    case ModuleId.Robotics:
      return { selectionColumn: "ID" };
    case ModuleId.Access:
    case ModuleId.AIM:
    default:
      return { selectionColumn: "ProductID" };
  }
}

export function SearchEditProductPage() {
  const { activeEndpoint } = useAuth();
  const moduleId = activeEndpoint?.ModuleID ?? 0;
  const config = useMemo(() => getModuleConfig(moduleId), [moduleId]);

  const gridRef = useRef<DataGridHandle | null>(null);
  const editor = useGridEditDialog({ gridRef, idColumn: config.selectionColumn });

  const dialog = editor.dialog;
  const productId = dialog?.kind === "edit" ? dialog.id : 0;
  const isOpen = dialog !== null;

  return (
    <SimplePageLayout title="Product Search">
      <DataGrid
        ref={gridRef}
        name="productSearch"
        onRowSelect={editor.handleRowSelect}
        onButtonClick={editor.handleButtonClick}
        toolbarExtra={
          <button onClick={editor.openCreate} className={styles.gridCreateBtn}>+ New Product</button>
        }
      />

      {isOpen && (
        <ProductEditorModal
          key={productId}
          moduleId={moduleId}
          productId={productId}
          onClose={editor.close}
          onSaved={editor.onSaved}
        />
      )}
    </SimplePageLayout>
  );
}

/* ── Shared modal wrapper ──────────────────────────────
   Raw `Modal` primitives (wide-form-editor exception per AGENTS.md
   § Create/Edit Modal Guidelines). Save lives in `ModalFooter`, auxiliary
   buttons bubbled from the inner editor sit left-aligned in the same footer,
   status message sits above the footer. Used by both the search page and the
   PagePopup entry so behaviour is identical regardless of entry path. */
interface ProductEditorModalProps {
  moduleId: number;
  productId: number;
  prefill?: ProductEditorPrefill;
  onClose: () => void;
  onSaved: () => void;
}

function ProductEditorModal({ moduleId, productId, prefill, onClose, onSaved }: ProductEditorModalProps) {
  const editorRef = useRef<ProductEditorHandle | null>(null);
  const [state, setState] = useState<EditorState>({ saving: false, status: null });
  const [aux, setAux] = useState<ReactNode>(null);

  const isNew = productId === 0;
  const statusText = state.status
    ? (state.status.type === "error" && !import.meta.env.DEV && /System\.\w*Exception/.test(state.status.text)
        ? "An unexpected error occurred."
        : state.status.text)
    : null;

  return (
    <GridEditModal
      title={isNew ? "New Product" : `Edit Product #${productId}`}
      wide
      maxWidth="1400px"
      saving={state.saving}
      saveLabel={isNew ? "Create" : "Save"}
      savingLabel={isNew ? "Creating..." : "Saving..."}
      statusMessage={state.status ? { type: state.status.type, message: statusText ?? "" } : null}
      unsaved={!!state.dirty}
      footerLeft={aux}
      onClose={onClose}
      onSave={() => editorRef.current?.save()}
    >
      <ProductEditRouter
        ref={editorRef}
        moduleId={moduleId}
        productId={productId}
        prefill={prefill}
        onSaved={onSaved}
        onStateChange={setState}
        onAuxiliaryChange={setAux}
      />
    </GridEditModal>
  );
}

/* ── Module router ─────────────────────────────────────── */

interface ProductEditRouterProps {
  moduleId: number;
  productId: number;
  prefill?: ProductEditorPrefill;
  onSaved: () => void;
  onStateChange: (state: EditorState) => void;
  onAuxiliaryChange: (node: ReactNode) => void;
}

const ProductEditRouter = forwardRef<ProductEditorHandle, ProductEditRouterProps>(
  function ProductEditRouter(
    { moduleId, productId, prefill, onSaved, onStateChange, onAuxiliaryChange }: ProductEditRouterProps,
    ref: Ref<ProductEditorHandle>,
  ) {
    const common = { productId, prefill, onSaved, onStateChange, onAuxiliaryChange };
    switch (moduleId) {
      case ModuleId.Rent:
      case ModuleId.Game:
      case ModuleId.GameRent:
        return <EditProductMovie ref={ref} {...common} />;
      case ModuleId.CompuShop:
        return <EditProductCS ref={ref} {...common} />;
      case ModuleId.NeoCaddie:
        return <EditProductNeoCaddie ref={ref} {...common} />;
      case ModuleId.Robotics:
        return <EditProductRobotics ref={ref} {...common} />;
      case ModuleId.Access:
      case ModuleId.AIM:
      default:
        return <EditProductAccess ref={ref} moduleId={moduleId} {...common} />;
    }
  },
);

/* ── Standalone popup for PagePopup integration ────────
   Reaches here from hyperlinkDispatcher when a product link is clicked
   outside the Product Search grid. Uses the same `ProductEditorModal` so
   the footer contract is identical. */
interface ProductPopupProps {
  editId?: string;
  /** Values to pre-apply for review (see ProductEditorPrefill). */
  prefill?: ProductEditorPrefill;
  onClose: () => void;
  /** Optional save-distinct callback so PagePopup can tell the grid that a
   *  save occurred. When omitted, save and cancel collapse to onClose
   *  (legacy refresh-always behaviour). */
  onSaved?: () => void;
}

export function ProductPopup({ editId, prefill, onClose, onSaved }: ProductPopupProps) {
  const { activeEndpoint } = useAuth();
  const moduleId = activeEndpoint?.ModuleID ?? 0;
  const productId = parseInt(editId ?? "0", 10) || 0;

  return (
    <ProductEditorModal
      key={productId}
      moduleId={moduleId}
      productId={productId}
      prefill={prefill}
      onClose={onClose}
      onSaved={onSaved ?? onClose}
    />
  );
}
